import { localizationStoreInstance } from 'src/feature/localization/store/localization.store'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { SkillGapInsightsResponse } from '../model/my-team-skill-gaps'

const MAX_RATING = 5
const CRITICAL_GAP_THRESHOLD = 1.5
const NEEDS_TRAINING_GAP_THRESHOLD = 0.5

type RatingTier = 'low' | 'medium' | 'high' | 'veryHigh'
type MetricItem = {
  label: string
  value: string | number
  tootlTipDesc: string
  valueColor?: boolean
}

const clampRating = (rating: number) => Math.max(0, Math.min(MAX_RATING, rating))

const getRatingTier = (rating: number): RatingTier => {
  if (rating >= 5) {
    return 'veryHigh'
  }
  if (rating >= 4) {
    return 'high'
  }
  if (rating >= 3) {
    return 'medium'
  }

  return 'low'
}

export const getColorByRating = (rating: number): string => {
  const ratingTier = getRatingTier(rating)

  if (ratingTier === 'veryHigh' || ratingTier === 'high') {
    return Palette.OLIVE_GREEN
  }
  if (ratingTier === 'medium') {
    return Palette.GOLDEN_ORANGE
  }

  return Palette.DANGER
}

export const calculateRatingFillWidth = (rating: number): string => `${(clampRating(rating) / MAX_RATING) * 100}%`

export const getCardRatingColor = (rating: number): string => {
  const ratingTier = getRatingTier(rating)

  if (ratingTier === 'veryHigh') {
    return ColorTokens.primary200
  }
  if (ratingTier === 'high') {
    return ColorTokens.secondary200
  }
  if (ratingTier === 'medium') {
    return ColorTokens.yellow100
  }

  return ColorTokens.red100
}

export const getRatingLevelLabel = (rating: number): string => {
  const ratingLevels = localizationStoreInstance.translation.pages.myTeam.ratingLevels
  const normalizedRating = clampRating(rating)

  if (normalizedRating > 4) {
    return ratingLevels.expert()
  }
  if (normalizedRating > 3) {
    return ratingLevels.advanced()
  }
  if (normalizedRating > 2) {
    return ratingLevels.professional()
  }
  if (normalizedRating > 1) {
    return ratingLevels.experienced()
  }

  return ratingLevels.beginner()
}

export const getProgressFillStyle = (width: string, color: string, zIndex: number, opacity?: number) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  transition: 'width 0.3s ease',
  overflow: 'hidden',
  borderRadius: '10px',
  width,
  backgroundColor: color,
  zIndex,
  ...(opacity ? { opacity } : {}),
})

export const getGapStatus = (gap: number): { status: string, color: string } => {
  const pageTranslation = localizationStoreInstance.translation.pages.myTeam.skillGapInsights

  if (gap > CRITICAL_GAP_THRESHOLD) {
    return { status: pageTranslation.critical(), color: Palette.DANGER }
  }
  if (gap > NEEDS_TRAINING_GAP_THRESHOLD) {
    return { status: pageTranslation.needsTraining(), color: Palette.GOLDEN_ORANGE }
  }
  if (gap > 0) {
    return { status: pageTranslation.minorGap(), color: '#FFB84D' }
  }

  return { status: pageTranslation.meetsExceeds(), color: Palette.OLIVE_GREEN }
}

const createMetricItem = (label: string, value: string | number, tootlTipDesc: string, valueColor = false): MetricItem => ({
  label,
  value,
  tootlTipDesc,
  ...(valueColor ? { valueColor } : {}),
})

export const buildMetricsConfig = (insightsData?: SkillGapInsightsResponse) => {
  const metrics = insightsData?.metrics
  const pageTranslation = localizationStoreInstance.translation.pages.myTeam.skillGapInsights
  const metricsHeader = localizationStoreInstance.translation.pages.myTeam.metricsHeader

  return {
    skillInsights: [
      createMetricItem(metricsHeader.totalSkills(), metrics?.totalSkills ?? '-', pageTranslation.totalSkillsTooltip()),
      createMetricItem(`${metricsHeader.highestRated()} (4-5★)`, metrics?.highRated ?? '-', pageTranslation.highRatedTooltip()),
      createMetricItem(`${metricsHeader.mediumRated()} (2-3★)`, metrics?.mediumRated ?? '-', pageTranslation.mediumRatedTooltip()),
      createMetricItem(`${metricsHeader.lowestRated()} (1★)`, metrics?.needsAttention ?? '-', pageTranslation.needsAttentionTooltip()),
    ],
    userInsights: [
      createMetricItem(metricsHeader.totalUsers(), metrics?.totalUsers ?? '-', pageTranslation.totalUsersTooltip()),
      createMetricItem(metricsHeader.avgSkillRating(), metrics ? metrics.avgTeamRating.toFixed(2) : '-', pageTranslation.avgTeamRatingTooltip()),
      createMetricItem(metricsHeader.avgExpectedRating(), metrics ? metrics.avgExpectedRating.toFixed(2) : '-', pageTranslation.avgExpectedRatingTooltip()),
      createMetricItem(metricsHeader.criticalGaps(), metrics ? `${metrics.criticalGapPercentage}%` : '-', pageTranslation.criticalGapPercentageTooltip()),
      createMetricItem(metricsHeader.meetsExpectations(), metrics ? `${metrics.overperformerPercentage}%` : '-', pageTranslation.overperformerPercentageTooltip()),
      createMetricItem(pageTranslation.gap(), metrics ? `${metrics.avgSkillGap.toFixed(2)} ★` : '-', pageTranslation.avgSkillGapTooltip()),
      createMetricItem(metricsHeader.highestRated(), metrics ? `${metrics.excellenceRate}%` : '-', pageTranslation.excellenceRateTooltip()),
      createMetricItem(metricsHeader.expectations(), metrics ? `${metrics.teamReadinessScore}%` : '-', pageTranslation.teamReadinessScoreTooltip(), true),
    ],
  }
}
